"use client";

/**
 * DiscoveryFanbasesRow 컴포넌트
 * @description Discovery 의 팬베이스 가로줄. 아바타 카드는 글자 폭이 카드 폭이라 간격을 `md` 로
 *   벌린다 — 8 이면 이름끼리 붙어 읽힌다.
 */
import { memo, useCallback, useMemo, useState } from "react";

import { discoveryFanbaseStyles } from "./DiscoveryFanbases.styles";
import type { DiscoveryFanbasesRowProps } from "./DiscoveryFanbases.types";
import DiscoveryFanbasesCard from "./DiscoveryFanbasesCard";
import DiscoveryFanbasesSheet from "./DiscoveryFanbasesSheet";

import { FANBASE_ROW_PAGE_SIZE } from "./DiscoveryFanbases.constants";
import { useSliderPages } from "../useSliderPages";

import { loadFanbases } from "../../actions/loadFanbases";
import { EXPLORE_COPY } from "../../constants/copy";
import type { FanbaseItem } from "../../dtos";
import { Reveal, Section, SectionHeader, Slider } from "@/shared/ui";

const keyExtractor = (fanbase: FanbaseItem): string => fanbase.handle;

const loadPage = (cursor: string) =>
  loadFanbases(FANBASE_ROW_PAGE_SIZE, cursor);

const DiscoveryFanbasesRow = memo<DiscoveryFanbasesRowProps>(({ firstPage }) => {
  const { items, loadMore } = useSliderPages(firstPage, loadPage);

  /* 열린 팬베이스는 handle 로 기억한다 — 목록이 바뀌어도 신원이 흔들리지 않는다 */
  const [selectedHandle, setSelectedHandle] = useState<string | null>(null);
  const selected = useMemo(
    () => items.find((fanbase) => fanbase.handle === selectedHandle) ?? null,
    [items, selectedHandle],
  );
  const close = useCallback(() => setSelectedHandle(null), []);

  const renderItem = useCallback(
    (fanbase: FanbaseItem, index: number) => (
      <DiscoveryFanbasesCard
        fanbase={fanbase}
        index={index}
        onPress={setSelectedHandle}
      />
    ),
    [],
  );

  return (
    <div className={discoveryFanbaseStyles.root}>
      <DiscoveryFanbasesSheet fanbase={selected} onClose={close} />
      <Section padding="sm">
        <Reveal>
          <SectionHeader title={EXPLORE_COPY.fanbases.title} />
        </Reveal>
      </Section>
      <Slider
        navigationLabel={EXPLORE_COPY.fanbases.title}
        padding="sm"
        gap="md"
        /* 카드가 hover 에서 떠오른다 — 스크롤 상자가 위아래를 잘라 내지 않게 자리를 낸다 */
        bleed="shadow"
        data={items}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        onEndReached={loadMore}
      />
    </div>
  );
});

DiscoveryFanbasesRow.displayName = "DiscoveryFanbasesRow";

export default DiscoveryFanbasesRow;
